import {
  BufferAttribute,
  BufferGeometry,
  Group,
  Line,
  LineBasicMaterial,
  Points,
  PointsMaterial,
  Vector3,
} from 'three'
import type { RoadSpline } from '../core/spline/splineGenerator'
import { convertPosition } from '../core/unity'
import type { GameSession } from '../game/session'

/**
 * Debug overlay for the curves the gameplay actually runs on.
 *
 * Two things are worth seeing, and neither is visible from the road mesh:
 *
 *  - **The whole road spline**, including the entry/exit extensions the mesh
 *    clips away. Buses wrap around the full curve, so the stretch beyond the
 *    tarmac is where they vanish into the tunnel and reappear.
 *  - **Each merging bus's entry spline** — the throwaway curve from
 *    `CreateEntrySpline`: a bridge from the bus's nose out to the road, then the
 *    road itself. Seeing the join makes it obvious whether
 *    `FindNearestFlowAlignedEntry` picked a sane spot.
 *
 * Everything is drawn slightly above the road so it does not z-fight.
 */

const Y_LIFT = 0.55

const COLOURS = {
  /** Full spline, extensions included. */
  spline: 0xff3fa4,
  /** The stretch the road mesh actually covers. */
  clipped: 0x00e5ff,
  /** Spline control points after smoothing. */
  control: 0xffe000,
  /** Nodes as the level author placed them. */
  node: 0xf2f2f2,
  /** Entry curve: bridge out of the lot. */
  bridge: 0xff9400,
  /** Entry curve: the road section it continues onto. */
  entryRoad: 0x00cc1a,
  /** Where the bridge meets the road. */
  join: 0xe50000,
  /** Exit curve: the run-off a full bus departs along. */
  exit: 0xff5cf0,
} as const

export class SplineDebug {
  readonly object = new Group()
  private readonly disposables: { dispose(): void }[] = []
  private readonly entryLines: Line[] = []
  private readonly joinMarkers: Points[] = []

  constructor(road: RoadSpline, session: GameSession) {
    this.object.name = 'splineDebug'
    this.buildRoad(road, session)
  }

  private track<T extends { dispose(): void }>(d: T): T {
    this.disposables.push(d)
    return d
  }

  private line(points: Vector3[], colour: number, lift = Y_LIFT): Line {
    const array = new Float32Array(points.length * 3)
    points.forEach((p, i) => {
      array[i * 3] = p.x
      array[i * 3 + 1] = p.y + lift
      array[i * 3 + 2] = p.z
    })
    const geometry = this.track(new BufferGeometry())
    geometry.setAttribute('position', new BufferAttribute(array, 3))
    return new Line(geometry, this.track(new LineBasicMaterial({ color: colour })))
  }

  private dots(points: Vector3[], colour: number, size: number, lift = Y_LIFT): Points {
    const array = new Float32Array(points.length * 3)
    points.forEach((p, i) => {
      array[i * 3] = p.x
      array[i * 3 + 1] = p.y + lift
      array[i * 3 + 2] = p.z
    })
    const geometry = this.track(new BufferGeometry())
    geometry.setAttribute('position', new BufferAttribute(array, 3))
    return new Points(
      geometry,
      this.track(new PointsMaterial({ color: colour, size, sizeAttenuation: true })),
    )
  }

  // -------------------------------------------------------------------------

  private buildRoad(road: RoadSpline, session: GameSession): void {
    const { spline, originalStartPercent, originalEndPercent } = road
    const probe = new Vector3()

    // Whole curve, extensions included.
    const full: Vector3[] = []
    for (let i = 0; i <= 600; i++) {
      spline.evaluatePosition(i / 600, probe)
      full.push(convertPosition(probe, new Vector3()))
    }
    this.object.add(this.line(full, COLOURS.spline, Y_LIFT))

    // The stretch the road mesh covers.
    const clipped: Vector3[] = []
    for (let i = 0; i <= 400; i++) {
      const percent =
        originalStartPercent + ((originalEndPercent - originalStartPercent) * i) / 400
      spline.evaluatePosition(percent, probe)
      clipped.push(convertPosition(probe, new Vector3()))
    }
    this.object.add(this.line(clipped, COLOURS.clipped, Y_LIFT + 0.05))

    // Control points the smoothing pass produced, and the authored nodes.
    this.object.add(
      this.dots(
        spline.points.map((p) => convertPosition(p, new Vector3())),
        COLOURS.control,
        0.55,
      ),
    )
    this.object.add(
      this.dots(
        session.level.nodes.map((n) => convertPosition(n.position, new Vector3())),
        COLOURS.node,
        1.0,
        Y_LIFT + 0.4,
      ),
    )
  }

  // -------------------------------------------------------------------------

  /** Redraw the live entry curves. Call each frame. */
  sync(session: GameSession): void {
    if (!this.object.visible) return

    let used = 0
    // Join markers are counted separately: only entry curves have one, so
    // sharing the line counter would leave a stale marker behind whenever an
    // exit curve took a slot.
    let markers = 0
    for (const bus of session.buses) {
      const follower = session.followerOf(bus)
      if (!follower) continue

      // The exit curve, for a bus running off after filling up. Drawn whole —
      // unlike the entry curve it has no join to split at, it just leaves.
      const exit = follower.exitPath
      if (exit) {
        const points = exit.samplePoints.map((p) => convertPosition(p, new Vector3()))
        this.setEntryLine(used * 2, points, COLOURS.exit)
        this.setEntryLine(used * 2 + 1, [], COLOURS.exit)
        used++
        continue
      }

      const path = follower.entryPath
      if (!path) continue

      const points = path.samplePoints.map((p) => convertPosition(p, new Vector3()))
      const bridge = points.slice(0, path.joinIndex + 1)
      const tail = points.slice(path.joinIndex)

      this.setEntryLine(used * 2, bridge, COLOURS.bridge)
      this.setEntryLine(used * 2 + 1, tail, COLOURS.entryRoad)
      this.setJoinMarker(markers, points[path.joinIndex])
      markers++
      used++
    }

    for (let i = used * 2; i < this.entryLines.length; i++) this.entryLines[i].visible = false
    for (let i = markers; i < this.joinMarkers.length; i++) this.joinMarkers[i].visible = false
  }

  /**
   * Entry curves come and go every time a bus merges, so the geometry is
   * rewritten in place rather than rebuilt — allocating a fresh Line per bus
   * per frame would churn buffers for no reason.
   */
  private setEntryLine(slot: number, points: Vector3[], colour: number): void {
    if (points.length === 0) {
      if (slot < this.entryLines.length) this.entryLines[slot].visible = false
      return
    }
    while (this.entryLines.length <= slot) {
      const geometry = this.track(new BufferGeometry())
      geometry.setAttribute('position', new BufferAttribute(new Float32Array(3 * 64), 3))
      const line = new Line(geometry, this.track(new LineBasicMaterial({ color: colour })))
      line.frustumCulled = false
      this.object.add(line)
      this.entryLines.push(line)
    }

    const line = this.entryLines[slot]
    ;(line.material as LineBasicMaterial).color.setHex(colour)

    // Grow the buffer to fit.
    //
    // A fixed 64 was enough when the entry curve was a couple of dozen control
    // points; it now carries ~180 samples, so the bridge half was being drawn up
    // to point 64 and simply stopping, while the road half started from the join
    // index further along. The gap between the two read as a kink in the curve
    // when the curve itself was smooth.
    let attribute = line.geometry.getAttribute('position') as BufferAttribute
    if (attribute.count < points.length) {
      const grown = new BufferAttribute(new Float32Array(3 * Math.max(64, points.length * 2)), 3)
      line.geometry.setAttribute('position', grown)
      attribute = grown
    }

    for (let i = 0; i < attribute.count; i++) {
      // Repeat the last real point past the end; a degenerate tail draws nothing
      // rather than a stray segment back to the origin.
      const p = points[Math.min(i, points.length - 1)] ?? points[0]
      attribute.setXYZ(i, p.x, p.y + Y_LIFT + 0.1, p.z)
    }
    attribute.needsUpdate = true
    line.geometry.setDrawRange(0, points.length)
    line.geometry.computeBoundingSphere()
    line.visible = points.length > 1
  }

  private setJoinMarker(slot: number, point: Vector3): void {
    while (this.joinMarkers.length <= slot) {
      const geometry = this.track(new BufferGeometry())
      geometry.setAttribute('position', new BufferAttribute(new Float32Array(3), 3))
      const marker = new Points(
        geometry,
        this.track(new PointsMaterial({ color: COLOURS.join, size: 1.4, sizeAttenuation: true })),
      )
      marker.frustumCulled = false
      this.object.add(marker)
      this.joinMarkers.push(marker)
    }
    const marker = this.joinMarkers[slot]
    const attribute = marker.geometry.getAttribute('position') as BufferAttribute
    attribute.setXYZ(0, point.x, point.y + Y_LIFT + 0.3, point.z)
    attribute.needsUpdate = true
    marker.visible = true
  }

  dispose(): void {
    for (const d of this.disposables) d.dispose()
    this.object.removeFromParent()
  }
}

/** Legend, so the colours mean something without reading the source. */
export const SPLINE_DEBUG_LEGEND: readonly { colour: number; label: string }[] = [
  { colour: COLOURS.spline, label: 'road spline (full, incl. tunnel extensions)' },
  { colour: COLOURS.clipped, label: 'visible road (clip range)' },
  { colour: COLOURS.control, label: 'control points (after smoothing)' },
  { colour: COLOURS.node, label: 'authored nodes' },
  { colour: COLOURS.bridge, label: 'entry: bridge out of the lot' },
  { colour: COLOURS.entryRoad, label: 'entry: road section' },
  { colour: COLOURS.join, label: 'entry join' },
  { colour: COLOURS.exit, label: 'exit: run-off after departing' },
]
